import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useReactFlow, getNodesBounds } from 'reactflow';
import { PAGE_SIZE_DIMENSIONS, PAGE_SIZE_A4 } from '../../../utilities/constants/pageSizeConstants';
import html2canvas from 'html2canvas';
// We prefer html2canvas for multipage because we can control transform per-clone reliably
import { openModal } from '../../../redux/reducers/global/modal/modalSlice';
import { registerModalTask } from '../modal/modalTaskRegistry';
import { MODAL_TYPES } from '../modal/modalTypes';
import { clearSelection as clearNodeSelection, setSelectedNodeIds } from '../../../redux/reducers/global/node/nodeSlice';
import { clearSelection as clearEdgeSelection, setSelectedEdgeIds } from '../../../redux/reducers/global/edge/edgeSlice';
import {
  createCompressedTileCanvas,
  downloadCanvasAsPng,
} from '../../../utilities/export/rasterCompression';

const DownloadButton = ({ children }) => {
  const { getNodes } = useReactFlow();
  const dispatch = useDispatch();
  const pageSize = useSelector((state) => state.view.pageSize);
  const selectedNodeIds = useSelector((state) => state.node.selectedNodeIds) || [];
  const selectedEdgeIds = useSelector((state) => state.edge.selectedEdgeIds) || [];
  const { width, height } = PAGE_SIZE_DIMENSIONS[pageSize] || PAGE_SIZE_DIMENSIONS[PAGE_SIZE_A4];

  const performExport = async (updateProgress) => {
    const prevSelectedNodes = Array.isArray(selectedNodeIds) ? [...selectedNodeIds] : [];
    const prevSelectedEdges = Array.isArray(selectedEdgeIds) ? [...selectedEdgeIds] : [];
    updateProgress(0);
    const progressInterval = setInterval(() => {
      updateProgress((p) => (p < 90 ? p + 10 : p));
    }, 300);
    // Mark exporting to suppress focus outlines globally
    document.body && document.body.classList.add('exporting');
    dispatch(clearNodeSelection());
    dispatch(clearEdgeSelection());
    await new Promise((resolve) => requestAnimationFrame(resolve));
    // Hide UI elements and overlays that shouldn't appear in export
    const resizeControls = document.querySelectorAll('.react-flow__resize-control');
    const handles = document.querySelectorAll('.react-flow__handle');
    const minimaps = document.querySelectorAll('.react-flow__minimap');
    const controls = document.querySelectorAll('.reactflow-controls, .react-flow__controls');
    const uxOnly = document.querySelectorAll('.ux-only:not(.export-visible)');
    const selectedBorders = document.querySelectorAll('.selected, .selected-node');
    const selectedNodes = document.querySelectorAll('.node-wrapper.selected-node');
    const backgrounds = document.querySelectorAll('.react-flow__background');
    const selectionBoxes = document.querySelectorAll('.react-flow__selection');
    const attributions = document.querySelectorAll('.react-flow__attribution');
    const pageGrid = document.querySelectorAll('.page-grid-overlay, .gray-overlay');
    resizeControls.forEach(el => el.classList.add('hide-element'));
    handles.forEach(el => el.classList.add('hide-element'));
    selectedBorders.forEach(el => el.classList.add('no-border'));
    selectedNodes.forEach(el => { el.dataset.wasSelectedNode = 'true'; el.classList.remove('selected-node'); });
    backgrounds.forEach(el => el.classList.add('hide-element'));
    minimaps.forEach(el => el.classList.add('hide-element'));
    controls.forEach(el => el.classList.add('hide-element'));
    uxOnly.forEach(el => el.classList.add('hide-element'));
    selectionBoxes.forEach(el => el.classList.add('hide-element'));
    attributions.forEach(el => el.classList.add('hide-element'));
    pageGrid.forEach(el => el.classList.add('hide-element'));

    // Current bounding box of all nodes
    const nodesArrForDebug = getNodes();
    const nodesBounds = getNodesBounds(nodesArrForDebug);

    // Determine used pages strictly from node bounding-box (to match UI tiles)
    const nb = nodesBounds;
    const minIx = Math.floor(nb.x / width);
    const maxIx = Math.floor((nb.x + Math.max(0, nb.width - 0.001)) / width);
    const minIy = Math.floor(nb.y / height);
    const maxIy = Math.floor((nb.y + Math.max(0, nb.height - 0.001)) / height);
    const pages = [];
    for (let iy = minIy; iy <= maxIy; iy++) {
      for (let ix = minIx; ix <= maxIx; ix++) pages.push({ ix, iy });
    }
    if (pages.length === 0) pages.push({ ix: 0, iy: 0 });

    const containerEl = document.querySelector('.react-flow');
    const viewport = document.querySelector('.react-flow__viewport');

    // Pre-fetch and inline images in the CLONE to avoid CORS/base-path issues
    const imgs = Array.from(viewport.querySelectorAll('img'));
    const uniqueSrcs = Array.from(new Set(imgs.map((i) => i.getAttribute('src')).filter(Boolean)));
    const srcToDataUrl = new Map();

    const fetchAsDataUrl = async (src) => {
      const abs = new URL(src, document.baseURI).toString();
      try {
        const res = await fetch(abs, { credentials: 'same-origin' });
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('svg') || /\.svg(\?|#|$)/i.test(abs)) {
          const text = await res.text();
          return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(text);
        } else {
          const blob = await res.blob();
          return await new Promise((resolve) => {
            const fr = new FileReader();
            fr.onload = () => resolve(fr.result);
            fr.readAsDataURL(blob);
          });
        }
      } catch (e) {
        return abs;
      }
    };

    await Promise.all(uniqueSrcs.map(async (s) => {
      const dataUrl = await fetchAsDataUrl(s);
      srcToDataUrl.set(new URL(s, document.baseURI).toString(), dataUrl);
    }));
    // Render one big canvas covering all tiles and then slice per page
    const minIxAll = minIx;
    const minIyAll = minIy;
    const tilesWide = (maxIx - minIx + 1);
    const tilesHigh = (maxIy - minIy + 1);
    const totalW = tilesWide * width;
    const totalH = tilesHigh * height;

    const fullCanvas = await html2canvas(containerEl, {
      backgroundColor: '#fff', width: totalW, height: totalH, scale: 1,
      useCORS: true, allowTaint: true, imageTimeout: 0, foreignObjectRendering: true,
      scrollX: 0, scrollY: 0,
      onclone: (doc) => {
        // stylesheet for edges
        try {
          const style = doc.createElement('style');
          style.textContent = `
            .react-flow__edges path{stroke:#222!important;stroke-width:3!important;opacity:1!important;fill:none!important;}
            .attr-row,.attr-row *{white-space:nowrap!important;word-break:keep-all!important;}
            .attr-row{column-gap:8px!important;}
            .attr-row__value{margin-left:0!important;}
          `;
          doc.head && doc.head.appendChild(style);
        } catch(_) { }
        const cont = doc.querySelector('.react-flow');
        if (!cont) return;
        try { doc.body.style.margin = '0'; doc.documentElement.style.margin = '0'; } catch(_){ }
        const host = doc.createElement('div'); host.style.position='fixed'; host.style.left='0'; host.style.top='0'; host.style.width=`${totalW}px`; host.style.height=`${totalH}px`; host.style.overflow='hidden'; host.style.background='#fff';
        doc.body.innerHTML=''; doc.body.appendChild(host);
        const clip = doc.createElement('div'); clip.style.position='relative'; clip.style.width=`${totalW}px`; clip.style.height=`${totalH}px`; clip.style.overflow='hidden';
        const pad = doc.createElement('div'); pad.style.position='absolute'; pad.style.left='0px'; pad.style.top='0px'; pad.style.width=`${totalW}px`; pad.style.height=`${totalH}px`;
        cont.style.margin='0'; cont.style.border='none'; cont.style.padding='0'; cont.style.position='relative'; cont.style.width=`${totalW}px`; cont.style.height=`${totalH}px`;
        pad.appendChild(cont); clip.appendChild(pad); host.appendChild(clip);
        const vp = clip.querySelector('.react-flow__viewport'); const edgesSvg = clip.querySelector('.react-flow__edges'); const rendererEl = clip.querySelector('.react-flow__renderer');
        if (rendererEl) { rendererEl.style.position='relative'; rendererEl.style.width='100%'; rendererEl.style.height='100%'; }
        if (vp) { vp.style.transform='none'; vp.style.transformOrigin='0 0'; vp.style.position='absolute'; vp.style.left=`${-minIxAll*width}px`; vp.style.top=`${-minIyAll*height}px`; }
        if (edgesSvg) { edgesSvg.style.transform='none'; edgesSvg.style.transformOrigin='0 0'; edgesSvg.style.position='absolute'; edgesSvg.style.left=`${-minIxAll*width}px`; edgesSvg.style.top=`${-minIyAll*height}px`; edgesSvg.setAttribute('width', `${totalW}`); edgesSvg.setAttribute('height', `${totalH}`); }
        // Inline images
        const cImgs = vp ? vp.querySelectorAll('img') : []; cImgs && cImgs.forEach((img)=>{ try{ const src=img.getAttribute('src'); if(!src) return; const abs=new URL(src, document.baseURI).toString(); const mapped = srcToDataUrl.get(abs); if(mapped){ img.setAttribute('crossorigin','anonymous'); img.setAttribute('src', mapped);} }catch(_){}});
        // Inline SVGs
        const inlineSvgs = clip.querySelectorAll('.inline-svg-container svg');
        inlineSvgs.forEach((svg) => {
          try {
            const parent = svg.parentElement;
            const parseDim = (val) => {
              if (typeof val === 'number') return val;
              if (!val) return NaN;
              const numeric = parseFloat(val);
              return Number.isFinite(numeric) && numeric > 0 ? numeric : NaN;
            };
            const datasetWidth = parent ? parseDim(parent.getAttribute('data-inline-width') || (parent.dataset && parent.dataset.inlineWidth)) : NaN;
            const datasetHeight = parent ? parseDim(parent.getAttribute('data-inline-height') || (parent.dataset && parent.dataset.inlineHeight)) : NaN;
            let widthPx = Number.isFinite(datasetWidth) ? datasetWidth : svg.getBoundingClientRect().width;
            let heightPx = Number.isFinite(datasetHeight) ? datasetHeight : svg.getBoundingClientRect().height;
            if ((!Number.isFinite(widthPx) || widthPx <= 0 || !Number.isFinite(heightPx) || heightPx <= 0) && svg.getAttribute('viewBox')) {
              const [, , vbW, vbH] = svg.getAttribute('viewBox').split(/[\s,]+/).map((n) => Number(n));
              if (Number.isFinite(vbW) && Number.isFinite(vbH) && vbW > 0 && vbH > 0) {
                if (!Number.isFinite(widthPx) || widthPx <= 0) {
                  widthPx = Number.isFinite(heightPx) && heightPx > 0 ? (heightPx * vbW) / vbH : vbW;
                }
                if (!Number.isFinite(heightPx) || heightPx <= 0) {
                  heightPx = Number.isFinite(widthPx) && widthPx > 0 ? (widthPx * vbH) / vbW : vbH;
                }
              }
            }
            if (!Number.isFinite(widthPx) || widthPx <= 0) widthPx = 1;
            if (!Number.isFinite(heightPx) || heightPx <= 0) heightPx = widthPx;

            svg.setAttribute('width', `${widthPx}`);
            svg.setAttribute('height', `${heightPx}`);
            svg.style.width = `${widthPx}px`;
            svg.style.height = `${heightPx}px`;
            svg.style.minWidth = `${widthPx}px`;
            svg.style.minHeight = `${heightPx}px`;
            svg.style.maxWidth = `${widthPx}px`;
            svg.style.maxHeight = `${heightPx}px`;
            if (!svg.getAttribute('xmlns')) {
              svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
            }
            if (!svg.getAttribute('preserveAspectRatio')) {
              svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
            }
          } catch (_) {}
        });
      }
    });

    // Slice and download
    const step = pages.length ? (90 / pages.length) : 90;
    let prog = 0;
    for (let i = 0; i < pages.length; i++) {
      const { ix, iy } = pages[i];
      const sx = (ix - minIxAll) * width;
      const sy = (iy - minIyAll) * height;
      const tile = createCompressedTileCanvas(fullCanvas, sx, sy, width, height);
      await downloadCanvasAsPng(tile, `diagram-${pageSize}-p${i+1}.png`);
      prog = Math.min(90, prog + step); updateProgress(prog);
    }

    // Restore hidden elements and original image sources
    resizeControls.forEach(el => el.classList.remove('hide-element'));
    handles.forEach(el => el.classList.remove('hide-element'));
    selectedBorders.forEach(el => el.classList.remove('no-border'));
    selectedNodes.forEach(el => { if (el.dataset.wasSelectedNode) { el.classList.add('selected-node'); delete el.dataset.wasSelectedNode; } });
    backgrounds.forEach(el => el.classList.remove('hide-element'));
    minimaps.forEach(el => el.classList.remove('hide-element'));
    controls.forEach(el => el.classList.remove('hide-element'));
    uxOnly.forEach(el => el.classList.remove('hide-element'));
    selectionBoxes.forEach(el => el.classList.remove('hide-element'));
    attributions.forEach(el => el.classList.remove('hide-element'));
    pageGrid.forEach(el => el.classList.remove('hide-element'));
    if (prevSelectedNodes.length > 0) {
      dispatch(setSelectedNodeIds(prevSelectedNodes));
    }
    if (prevSelectedEdges.length > 0) {
      dispatch(setSelectedEdgeIds(prevSelectedEdges));
    }
    clearInterval(progressInterval);
    updateProgress(100);
    document.body && document.body.classList.remove('exporting');
  };

  const onClick = () => {
    const taskId = registerModalTask(async (updateProgress) => {
      await performExport(updateProgress);
    });
    dispatch(openModal({
      modalType: MODAL_TYPES.EXPORT_EDITOR_PNG,
      modalProps: { pageSize, format: 'PNG', taskId }
    }));
  };

  return (
    <>
      <button className="download-btn" onClick={onClick}>
        {children}
      </button>
    </>
  );
};

export default DownloadButton;
